import {
  pgTable,
  text,
  boolean,
  timestamp,
  bigint,
  jsonb,
  integer,
  index,
  uniqueIndex,
  foreignKey,
  varchar,
  doublePrecision,
  numeric,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import {
  blockedIdentityType,
  invitationStatus,
  invitationChannel,
  notificationChannel,
  notificationType,
  oauthProvider,
  orderTargetType,
  userBlockAuditAction,
  userBlockReason,
  userDeletionReason,
} from './enums';
import { crmSync } from './auxiliary';
import { adminUser } from './admin';
import { file } from './files';
import { subscriptionPlan } from './subscription-plan';
import { pricingTier } from './pricing-tier';
import { messageTemplate } from './notification';

export interface UserNotificationPreferences {
  livestreamAutoSubscribe?: boolean;
}

export const user = pgTable(
  'User',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    telegramId: bigint({ mode: 'number' }).unique(),
    firstName: text(),
    lastName: text(),
    username: text(),
    email: text().unique(),
    isEmailVerified: boolean().default(false).notNull(),
    phone: text(),
    photoUrl: text(),
    photoId: integer(),
    about: text(),
    socialLinks: jsonb(),
    languagePreference: text().default('ru').notNull(),
    theme: text().default('system').notNull(),
    currencyPreference: text().default('USD'),
    timezone: text().default('UTC').notNull(),
    notificationSettings: jsonb().$type<UserNotificationPreferences | null>(),
    isPublicProfile: boolean().default(false).notNull(),
    isActive: boolean().default(true).notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
    cRmSyncId: integer(),
    lastCountry: varchar({ length: 2 }),
    lastCity: varchar({ length: 255 }),
    lastLatitude: doublePrecision(),
    lastLongitude: doublePrecision(),
    lastLocationAt: timestamp({ precision: 3, mode: 'date' }),
    deletedAt: timestamp({ precision: 3, mode: 'date' }),
    // Причина удаления (admin-delete и т.п.). NULL у активных пользователей.
    // Используется auth-handler'ом и аудитом в админке.
    deletionReason: userDeletionReason('deletionReason'),
    // Кто инициировал удаление (AdminUser.id). NULL у активных пользователей
    // и на случай системного удаления без привязки к админу.
    deletedBy: integer('deletedBy'),
    // Блокировка пользователя админом (обратимая, в отличие от soft-delete).
    // При blockedAt != null auth-middleware возвращает 403 ACCOUNT_BLOCKED.
    // При unblock — все поля обнуляются, доступ возвращается.
    blockedAt: timestamp({ precision: 3, mode: 'date' }),
    blockedBy: integer('blockedBy'),
    blockReason: text('blockReason'),
    blockReasonCode: userBlockReason('blockReasonCode'),
    // Аналитика LTV/cohort: денормализованные поля, обновляемые AnalyticsDenormService
    // при каждом подтверждении платежа. firstPaymentAt и lastPaymentAt — null
    // у пользователей без оплат. totalSpentBase — суммарная выручка в базовой
    // валюте аналитики, purchaseCount — количество подтверждённых платежей.
    // acquisitionChannel — источник привлечения (direct/bot/referral/utm_*),
    // фиксируется при первом платеже и больше не меняется.
    firstPaymentAt: timestamp({ precision: 3, mode: 'date' }),
    lastPaymentAt: timestamp({ precision: 3, mode: 'date' }),
    totalSpentBase: numeric({ precision: 14, scale: 2 }).default('0').notNull(),
    purchaseCount: integer().default(0).notNull(),
    acquisitionChannel: text(),
    utmSource: text(),
    utmMedium: text(),
    utmCampaign: text(),
    utmTerm: text(),
    utmContent: text(),
    // FK -> Country.id, ON DELETE SET NULL. FK constraint объявлен в country.ts
    // через separate ALTER TABLE миграцию, чтобы избежать циклического импорта
    // user.ts <-> country.ts. Drizzle query builder работает без FK metadata.
    countryId: integer(),
    // Если аккаунт был смерджен в другой — здесь id выжившего (survivor).
    // Auth-handler'ы при попытке логина под этим userId редиректят на survivor.
    // FK на User.id (self-reference) добавляется отдельным ALTER TABLE в миграции.
    mergedIntoUserId: integer('mergedIntoUserId'),
  },
  (table) => [
    index('User_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
    index('User_languagePreference_idx').using('btree', table.languagePreference.asc().nullsLast()),
    index('User_deletedAt_idx').using('btree', table.deletedAt.asc().nullsLast()),
    index('User_deletedBy_idx').using('btree', table.deletedBy.asc().nullsLast()),
    index('User_firstPaymentAt_idx').using('btree', table.firstPaymentAt.asc().nullsLast()),
    index('User_acquisitionChannel_idx').using('btree', table.acquisitionChannel.asc().nullsLast()),
    index('User_totalSpentBase_idx').using('btree', table.totalSpentBase.desc().nullsLast()),
    index('User_utmSource_idx').using('btree', table.utmSource.asc().nullsLast()),
    index('User_countryId_idx').using('btree', table.countryId.asc().nullsLast()),
    foreignKey({
      columns: [table.photoId],
      foreignColumns: [file.id],
      name: 'User_photoId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.cRmSyncId],
      foreignColumns: [crmSync.id],
      name: 'User_cRMSyncId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.deletedBy],
      foreignColumns: [adminUser.id],
      name: 'User_deletedBy_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    index('User_blockedAt_idx').using('btree', table.blockedAt.asc().nullsLast()),
    foreignKey({
      columns: [table.blockedBy],
      foreignColumns: [adminUser.id],
      name: 'User_blockedBy_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    index('User_mergedIntoUserId_idx').using('btree', table.mergedIntoUserId.asc().nullsLast()),
  ],
);

/**
 * Таблица заблокированных идентификаторов (identity lock для удалённых юзеров).
 *
 * Когда юзер удаляется админом, все его identity-ключи (email, telegramId,
 * googleSub, appleSub, phone) записываются сюда ДО PII-очистки на User.
 * Auth-handler'ы (telegram-webapp, email login, google/apple OAuth) проверяют
 * эту таблицу перед созданием нового аккаунта / логином: если identity здесь —
 * отдают 403 ACCOUNT_BLOCKED.
 *
 * Структура независима от того, какие поля сохраняются на User: даже если email
 * обнуляется в PII-wipe, blocking-lock сохраняется здесь навсегда.
 *
 * Уникальный индекс по (identityType, identityValue) гарантирует, что один и
 * тот же identity нельзя добавить дважды.
 */
export const blockedIdentity = pgTable(
  'BlockedIdentity',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    identityType: blockedIdentityType('identityType').notNull(),
    identityValue: text('identityValue').notNull(),
    blockedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    // Для истории — ссылка на User.id, чей identity был заблокирован.
    // onDelete set null — чтобы запись о блокировке пережила hard-delete юзера.
    blockedUserId: integer('blockedUserId'),
    // Какой админ инициировал блокировку. nullable для системных сценариев.
    blockedBy: integer('blockedBy'),
    // Причина блокировки — mirrors userDeletionReason enum, но хранится текстом
    // чтобы дать больше гибкости в будущем (например, 'MANUAL_BLOCK' без удаления).
    reason: text('reason').notNull(),
  },
  (table) => [
    uniqueIndex('BlockedIdentity_type_value_key').using(
      'btree',
      table.identityType.asc().nullsLast(),
      table.identityValue.asc().nullsLast(),
    ),
    index('BlockedIdentity_blockedUserId_idx').using(
      'btree',
      table.blockedUserId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.blockedUserId],
      foreignColumns: [user.id],
      name: 'BlockedIdentity_blockedUserId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.blockedBy],
      foreignColumns: [adminUser.id],
      name: 'BlockedIdentity_blockedBy_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

/**
 * История действий блокировки/разблокировки пользователя админом.
 *
 * Пишется в `users.service.blockUser` и `unblockUser`. Хранит полный
 * audit-trail: кто, когда, за что заблокировал и кто когда разблокировал.
 * При unblock поля блокировки на `user` обнуляются, но запись в audit
 * остаётся — это единственный способ увидеть историю после снятия блока.
 */
export const userBlockAudit = pgTable(
  'UserBlockAudit',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    userId: integer('userId').notNull(),
    action: userBlockAuditAction('action').notNull(),
    // Для BLOCK — причина блокировки. Для UNBLOCK — опциональный комментарий админа.
    reason: text('reason'),
    // Для BLOCK — код причины. Для UNBLOCK — null.
    reasonCode: userBlockReason('reasonCode'),
    // Кто выполнил действие. Nullable на случай системных сценариев.
    performedBy: integer('performedBy'),
    performedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('UserBlockAudit_userId_idx').using(
      'btree',
      table.userId.asc().nullsLast(),
    ),
    index('UserBlockAudit_performedAt_idx').using(
      'btree',
      table.performedAt.desc().nullsLast(),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'UserBlockAudit_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.performedBy],
      foreignColumns: [adminUser.id],
      name: 'UserBlockAudit_performedBy_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

/**
 * История merge-операций: что и куда было слито.
 *
 * Пишется в `user-merge.service.mergeUsers` строго после soft-delete loser'а
 * в одной транзакции. Хранит снимок loser'а (loserSnapshot) и счётчики
 * перенесённых сущностей (transferredCounts) для аудита и потенциального
 * частичного отката силами поддержки.
 *
 * Запрос «откатить мердж» — это ручная операция: данные на survivor никуда
 * не делись, надо лишь восстановить loser из snapshot + переаттачить часть
 * сущностей обратно. На уровне сервиса автоматического отката нет.
 */
export const userMergeHistory = pgTable(
  'UserMergeHistory',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    // Кого слили (после мерджа: isActive=false, deletedAt!=null, mergedIntoUserId=survivorId).
    loserId: integer('loserId').notNull(),
    // Кто остался (выживший аккаунт, получивший данные loser'а).
    survivorId: integer('survivorId').notNull(),
    // Полный снимок строки User loser'а до мерджа: email, telegramId, phone,
    // photoUrl, имя, оплаты — всё, что может понадобиться для восстановления.
    loserSnapshot: jsonb('loserSnapshot').notNull(),
    // Счётчики перенесённых сущностей: { accessGrants: 1, orders: 0, ... }.
    // Нужны для UI confirm-диалога и для audit-просмотра.
    transferredCounts: jsonb('transferredCounts').notNull(),
    // Что инициировало мердж: 'TELEGRAM_LINK', 'EMAIL_LINK', 'ADMIN'.
    reason: text('reason').notNull(),
    // AdminUser.id если мердж инициирован из админки; null если пользователь сам.
    performedByAdminId: integer('performedByAdminId'),
    // Под каким userId юзер был залогинен в момент мерджа (= survivorId как правило).
    performedByUserId: integer('performedByUserId'),
    performedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('UserMergeHistory_loserId_idx').using('btree', table.loserId.asc().nullsLast()),
    index('UserMergeHistory_survivorId_idx').using('btree', table.survivorId.asc().nullsLast()),
    index('UserMergeHistory_performedAt_idx').using('btree', table.performedAt.desc().nullsLast()),
    foreignKey({
      columns: [table.loserId],
      foreignColumns: [user.id],
      name: 'UserMergeHistory_loserId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.survivorId],
      foreignColumns: [user.id],
      name: 'UserMergeHistory_survivorId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.performedByAdminId],
      foreignColumns: [adminUser.id],
      name: 'UserMergeHistory_performedByAdminId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.performedByUserId],
      foreignColumns: [user.id],
      name: 'UserMergeHistory_performedByUserId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

export const refreshToken = pgTable(
  'RefreshToken',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    tokenHash: varchar({ length: 512 }).notNull(),
    adminUserId: integer(),
    userId: integer(),
    expiresAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
    userAgent: varchar({ length: 512 }),
    ip: varchar({ length: 45 }),
    fingerprint: varchar({ length: 255 }),
    country: varchar({ length: 2 }),
    city: varchar({ length: 255 }),
    latitude: doublePrecision(),
    longitude: doublePrecision(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    lastUsedAt: timestamp({ precision: 3, mode: 'date' }),
    replacedAt: timestamp({ precision: 3, mode: 'date' }),
  },
  (table) => [
    index('RefreshToken_adminUserId_idx').using('btree', table.adminUserId.asc().nullsLast()),
    index('RefreshToken_expiresAt_idx').using('btree', table.expiresAt.asc().nullsLast()),
    index('RefreshToken_tokenHash_idx').using('btree', table.tokenHash.asc().nullsLast()),
    uniqueIndex('RefreshToken_tokenHash_key').using('btree', table.tokenHash.asc().nullsLast()),
    index('RefreshToken_userId_idx').using('btree', table.userId.asc().nullsLast()),
    foreignKey({
      columns: [table.adminUserId],
      foreignColumns: [adminUser.id],
      name: 'RefreshToken_adminUserId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'RefreshToken_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

export const notificationSetting = pgTable(
  'NotificationSetting',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    userId: integer().notNull(),
    channel: notificationChannel().notNull(),
    type: notificationType().notNull(),
    isEnabled: boolean().default(true).notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('NotificationSetting_userId_channel_type_key').using(
      'btree',
      table.userId.asc().nullsLast(),
      table.channel.asc().nullsLast(),
      table.type.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'NotificationSetting_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// Приглашения пользователей
export const userInvitation = pgTable(
  'UserInvitation',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    // Канал доставки: TELEGRAM (ЛС через GramJS) или EMAIL (письмо со ссылкой на платформу).
    channel: invitationChannel().default('TELEGRAM').notNull(),
    // Для TELEGRAM-канала обязателен, для EMAIL — null.
    telegramUserId: bigint({ mode: 'number' }),
    telegramUsername: text(),
    telegramFirstName: text(),
    telegramLastName: text(),
    telegramPhone: text(),
    // Для EMAIL-канала обязателен, для TELEGRAM — null.
    email: text(),
    userId: integer(),
    avatarFileId: integer(),
    templateId: integer(),
    subscriptionPlanId: integer(),
    pricingTierId: integer(),
    /**
     * Бот, через которого отправляется TELEGRAM-приглашение и формируется
     * ссылка t.me/<bot>?startapp=invite_<token>. NULL — fallback на первого
     * активного бота (legacy записи + EMAIL-приглашения, где deeplink не нужен).
     */
    botId: integer(),
    // Теги, которые автоматически выдаются приглашённому после accept.
    // Хранится массивом integer вместо отдельной join-таблицы — выдача одноразовая
    // (записывается в UserTag), редактировать после создания приглашения не требуется.
    tagIds: integer().array(),
    sentMessage: text(),
    status: invitationStatus().default('PENDING').notNull(),
    errorMessage: text(),
    invitedByAdminId: integer().notNull(),
    inviteToken: text().notNull(),
    sentAt: timestamp({ precision: 3, mode: 'date' }),
    acceptedAt: timestamp({ precision: 3, mode: 'date' }),
    expiresAt: timestamp({ precision: 3, mode: 'date' }),
    /**
     * Фиксированная дата окончания доступа, выдаваемого этим приглашением.
     * Если задана — подписка/доступ создаются с endDate = accessEndsAt,
     * независимо от durationDays тарифа и момента принятия.
     * NULL (default) — использовать стандартный durationDays тарифа.
     */
    accessEndsAt: timestamp({ precision: 3, mode: 'date' }),
    // Денормализация открытий email-приглашения (источник правды — EmailDelivery).
    // Заполняется при первом hit'е трекинг-пикселя; для TELEGRAM-канала остаётся NULL.
    emailFirstOpenedAt: timestamp({ precision: 3, mode: 'date' }),
    emailLastOpenedAt: timestamp({ precision: 3, mode: 'date' }),
    emailOpenCount: integer().default(0).notNull(),
    /**
     * Co-invite: второй член семьи, выдаваемый автоматически владельцу семейного
     * тарифа при accept этого приглашения. Заполняется только если выдаваемый
     * доступ — семейная подписка (SUBSCRIPTION_PLAN с isFamily=true у тарифа).
     * Контакт может быть нерезолвленным (только phone/username) — в этом случае
     * матчинг откладывается до регистрации второго члена семьи (см.
     * findPendingFamilyCoInviteForUser). NULL — приглашение без co-invite.
     */
    familyMemberTelegramUserId: bigint({ mode: 'number' }),
    familyMemberTelegramUsername: text(),
    familyMemberTelegramPhone: text(),
    /**
     * Проставляется, когда co-invite обработан (второй член добавлен в семью
     * или добавление отложено из-за отсутствия свободных слотов/ошибки).
     * NULL при familyMemberTelegramUserId/Username/Phone заданных = co-invite
     * ещё не обработан (ждёт accept владельца или регистрации второго).
     */
    familyMemberAddedAt: timestamp({ precision: 3, mode: 'date' }),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    // Частичный unique: один Telegram-пользователь — одно приглашение,
    // но только для строк с заполненным telegramUserId (EMAIL-приглашения не участвуют).
    uniqueIndex('UserInvitation_telegramUserId_key')
      .using('btree', table.telegramUserId.asc().nullsLast())
      .where(sql`${table.telegramUserId} IS NOT NULL`),
    // Частичный unique: один email — одно приглашение (только для EMAIL-канала).
    uniqueIndex('UserInvitation_email_key')
      .using('btree', table.email.asc().nullsLast())
      .where(sql`${table.email} IS NOT NULL`),
    uniqueIndex('UserInvitation_inviteToken_key').using(
      'btree',
      table.inviteToken.asc().nullsLast(),
    ),
    index('UserInvitation_status_idx').using('btree', table.status.asc().nullsLast()),
    index('UserInvitation_invitedByAdminId_idx').using(
      'btree',
      table.invitedByAdminId.asc().nullsLast(),
    ),
    // Отложенный матчинг нерезолвленных TELEGRAM-приглашений (requireResolved=false,
    // резолв через userbot не удался при создании) — при регистрации нового
    // пользователя ищем PENDING-инвайт по телефону/username, чтобы выдать доступы.
    // WHERE telegramUserId IS NULL: как только инвайт резолвлен (обычный флоу),
    // матчинг по этим полям больше не нужен — им владеет findPendingInvitationByTelegramId.
    index('UserInvitation_telegramPhone_unresolved_idx')
      .using('btree', table.telegramPhone.asc().nullsLast())
      .where(sql`${table.telegramUserId} IS NULL`),
    index('UserInvitation_telegramUsername_unresolved_idx')
      .using('btree', table.telegramUsername.asc().nullsLast())
      .where(sql`${table.telegramUserId} IS NULL`),
    // Отложенный матчинг co-invite члена семьи по телефону/username — при
    // регистрации нового пользователя ищем ACCEPTED-инвайт с необработанным
    // co-invite (familyMemberAddedAt IS NULL), чтобы добавить его в семью.
    index('UserInvitation_familyMemberPhone_pending_idx')
      .using('btree', table.familyMemberTelegramPhone.asc().nullsLast())
      .where(sql`${table.familyMemberAddedAt} IS NULL`),
    index('UserInvitation_familyMemberUsername_pending_idx')
      .using('btree', table.familyMemberTelegramUsername.asc().nullsLast())
      .where(sql`${table.familyMemberAddedAt} IS NULL`),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'UserInvitation_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.avatarFileId],
      foreignColumns: [file.id],
      name: 'UserInvitation_avatarFileId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.templateId],
      foreignColumns: [messageTemplate.id],
      name: 'UserInvitation_templateId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.invitedByAdminId],
      foreignColumns: [adminUser.id],
      name: 'UserInvitation_invitedByAdminId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.subscriptionPlanId],
      foreignColumns: [subscriptionPlan.id],
      name: 'UserInvitation_subscriptionPlanId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.pricingTierId],
      foreignColumns: [pricingTier.id],
      name: 'UserInvitation_pricingTierId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

// Полиморфный список доступов, выдаваемых при принятии приглашения.
// Один UserInvitation -> много UserInvitationItem.
// targetType: SUBSCRIPTION_PLAN | COURSE | CHANNEL_PASS | BOOK (повторно используем OrderTargetType — те же 4 продуктовых типа).
// targetId без FK constraint (полиморфно), валидация на уровне сервиса.
// pricingTierId — опциональная привязка тарифа (для SUBSCRIPTION_PLAN с тарифами обязателен, валидация на сервисе).
export const userInvitationItem = pgTable(
  'UserInvitationItem',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    invitationId: integer().notNull(),
    targetType: orderTargetType().notNull(),
    targetId: integer().notNull(),
    pricingTierId: integer(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('UserInvitationItem_invitationId_idx').using(
      'btree',
      table.invitationId.asc().nullsLast(),
    ),
    index('UserInvitationItem_target_idx').using(
      'btree',
      table.targetType.asc().nullsLast(),
      table.targetId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.invitationId],
      foreignColumns: [userInvitation.id],
      name: 'UserInvitationItem_invitationId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.pricingTierId],
      foreignColumns: [pricingTier.id],
      name: 'UserInvitationItem_pricingTierId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

/**
 * OAuthAccount — связь пользователя с внешним OAuth-провайдером (Google, Apple, ...).
 *
 * Один User может иметь несколько OAuthAccount (по одному на провайдера).
 * providerAccountId хранит устойчивый идентификатор провайдера (для Google —
 * поле `sub` из ID Token, стабильно на время жизни Google-аккаунта).
 *
 * Уникальный индекс (provider, providerAccountId) гарантирует, что один внешний
 * аккаунт не может быть привязан к двум User.
 *
 * Поля accessToken/refreshToken хранят последние выданные провайдером токены
 * (опционально, нужны только если backend делает запросы к API провайдера
 * от имени юзера). Для чистого SSO достаточно providerAccountId + email.
 */
export const oauthAccount = pgTable(
  'OAuthAccount',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    userId: integer('userId').notNull(),
    provider: oauthProvider('provider').notNull(),
    providerAccountId: text('providerAccountId').notNull(),
    email: text('email'),
    emailVerified: boolean('emailVerified').default(false).notNull(),
    name: text('name'),
    avatarUrl: text('avatarUrl'),
    // OAuth2-токены провайдера. Хранятся опционально — только если понадобится
    // вызывать API провайдера от имени юзера (например, Google Calendar).
    accessToken: text('accessToken'),
    refreshToken: text('refreshToken'),
    tokenExpiresAt: timestamp({ precision: 3, mode: 'date' }),
    scope: text('scope'),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('OAuthAccount_provider_providerAccountId_key').using(
      'btree',
      table.provider.asc().nullsLast(),
      table.providerAccountId.asc().nullsLast(),
    ),
    index('OAuthAccount_userId_idx').using('btree', table.userId.asc().nullsLast()),
    index('OAuthAccount_email_idx').using('btree', table.email.asc().nullsLast()),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'OAuthAccount_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);
